import {
    useInfiniteQuery,
    useQuery,
    keepPreviousData
} from '@tanstack/react-query';
import { BFFAPI } from '@newo/api/gateway';
import type {
    TPersona,
    ISessionsParams,
    TSessionsInfoParams,
    TAttributesParams,
    IConversationsUserPersonaParams,
    IConversationsActsParams
} from '@newo/api/gateway';
import axios from '@/utils/api/axiosInstance';

export const bffAPI = new BFFAPI(axios);

// GET Customer Attributes
export const useCustomerAttributesQuery = (params?: TAttributesParams) => {
    return useQuery({
        queryKey: ['bff/customer/attributes', params],
        queryFn: async () => bffAPI.getCustomerAttributes(params),
        placeholderData: keepPreviousData
    });
};

// GET Persona
export const usePersonaQuery = (personaId?: string) =>
    useQuery({
        queryKey: ['persona', personaId],
        queryFn: async (): Promise<TPersona> => {
            if (!personaId) {
                throw new Error('Persona ID is required');
            }
            return bffAPI.getPersona(personaId);
        },
        enabled: !!personaId
    });

// GET Sessions
export const useSessionsQuery = (params: ISessionsParams) => {
    return useQuery({
        queryKey: ['bff/sessions', params],
        queryFn: async () => {
            return bffAPI.getSessions(params);
        },
        gcTime: 0,
        staleTime: 0
    });
};

//GET Sessions Info
export const useSessionsInfoQuery = (params: TSessionsInfoParams) => {
    return useQuery({
        queryKey: ['bff/sessions/info', params],
        queryFn: async () => bffAPI.getSessionsInfo(params),
        placeholderData: keepPreviousData
    });
};

// GET Contacts
export const useContactsQuery = (params: IConversationsUserPersonaParams) => {
    return useQuery({
        queryKey: ['contacts', params],
        queryFn: async () => {
            return bffAPI.getConversationsUserPersonas(params);
        },
        placeholderData: keepPreviousData
    });
};

// GET Conversations acts& Infinite scroll
export const useActsPaginatedQuery = (
    params: IConversationsActsParams,
    isEnabled = true
) => {
    return useInfiniteQuery({
        queryKey: ['acts/paginated', params],
        queryFn: async ({ pageParam }) => {
            return bffAPI.getConversationsActs({
                ...params,
                page: pageParam
            });
        },
        initialPageParam: 1,
        enabled: isEnabled,
        getNextPageParam: (lastPage) => {
            const { page, per, total } = lastPage.metadata;
            if (page * per >= total) {
                return;
            }
            /* eslint-disable @typescript-eslint/no-unnecessary-type-conversion */
            return +page + 1;
        }
    });
};

// GET Widgets
export const useWidgetsQuery = (params: {
    widget_type: 'internal' | 'external';
}) => {
    return useQuery({
        queryKey: ['widgets', params],
        queryFn: async () => bffAPI.getWidgets(params)
    });
};
